import { Button, Drawer } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormIntegrationDto, IntegrationResponse, NewIntegrationBody } from '@/api';
import { ADAPTER_ORDER, adapterBlurb, adapterName } from '@/components/Integrations/adapters/copy';
import { FORMSHIVE_EVENT_TYPES, getAdapterMeta } from '@/components/Integrations/adapters/registry';
import {
  capabilitiesFor,
  useIntegrationCapabilities,
} from '@/components/Integrations/capabilities';
import { Tag } from '@/components/Ui/Tag';
import { useUndoToast } from '@/components/Ui/Toast';
import { IntegrationEditor } from '@/pages/Account/Integrations/IntegrationEditor';
import { SigningSecret } from '@/pages/Account/Integrations/SigningSecret';
import { useRustyState } from '@/state';
import classes from './AddIntegrationPanel.module.css';

const QUIET = { onSuccess: false } as const;

type Step =
  | { name: 'list' }
  | { name: 'pick' }
  | { name: 'config'; kind: string }
  | { name: 'secret'; kind: string; secret: string };

export interface AddIntegrationPanelProps {
  opened: boolean;
  onClose: () => void;
  form: { id: string; title: string };
  /** The form's current links; their integrations show as Linked. */
  links: FormIntegrationDto[];
  /** The account's integrations; null while loading or unavailable. */
  integrations: IntegrationResponse[] | null;
  /** Called after every link, create and undo, so the caller reloads its lists. */
  onChanged: () => Promise<void>;
}

const allEvents = () => FORMSHIVE_EVENT_TYPES.map((e) => e.type);

/** Existing integrations first with Link, then create-and-link through the adapter registry. */
export function AddIntegrationPanel({
  opened,
  onClose,
  form,
  links,
  integrations,
  onChanged,
}: AddIntegrationPanelProps) {
  const { t } = useTranslation();
  const notify = useUndoToast();
  const caps = useIntegrationCapabilities();
  const [step, setStep] = useState<Step>({ name: 'list' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const linked = new Set(links.map((l) => l.integration_id));
  const { api } = useRustyState.getState();

  const close = () => {
    setStep({ name: 'list' });
    onClose();
  };

  const unlink = async (integrationId: string) => {
    await api.unlinkFormIntegration(form.id, integrationId);
    await onChanged();
  };

  const link = async (integration: IntegrationResponse) => {
    setBusyId(integration.id);
    try {
      await api.linkFormIntegration(
        form.id,
        { integration_id: integration.id, event_types: allEvents(), enabled: true },
        QUIET
      );
      await onChanged();
      notify(t('form.addIntegration.linked', { title: integration.title }), () =>
        unlink(integration.id)
      );
    } catch {
      // the API layer reports the error
    } finally {
      setBusyId(null);
    }
  };

  const create = async (kind: string, body: NewIntegrationBody) => {
    const created = await api.newIntegration(body, QUIET);
    await api.linkFormIntegration(
      form.id,
      { integration_id: created.id, event_types: allEvents(), enabled: true },
      QUIET
    );
    await onChanged();
    notify(t('form.addIntegration.created', { title: created.title }), async () => {
      await api.unlinkFormIntegration(form.id, created.id);
      await api.deleteIntegration(created.id);
      await onChanged();
    });
    setStep(
      created.hmac_secret_b64
        ? { name: 'secret', kind, secret: created.hmac_secret_b64 }
        : { name: 'list' }
    );
  };

  const note = (i: IntegrationResponse) => {
    const parts: string[] = [adapterName(i.kind, t)];
    if (typeof i.form_count === 'number') {
      parts.push(t('form.addIntegration.usedBy', { count: i.form_count }));
    }
    if (i.failed_7d > 0) parts.push(t('form.addIntegration.failed', { count: i.failed_7d }));
    if (i.signed) parts.push(t('form.addIntegration.signed'));
    return parts.join(' · ');
  };

  const title =
    step.name === 'list'
      ? t('form.addIntegration.title')
      : step.name === 'pick'
        ? t('form.addIntegration.newTitle')
        : step.name === 'config'
          ? t('form.addIntegration.configTitle', { name: adapterName(step.kind, t) })
          : t('integrations.signing.title');

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      size={500}
      title={
        <span className={classes.titles}>
          <span className={classes.title}>{title}</span>
          <span className={classes.help}>{form.title}</span>
        </span>
      }
      classNames={{ body: classes.body }}
    >
      {step.name === 'list' && (
        <div className={classes.stack}>
          <p className={classes.sub}>{t('form.addIntegration.existingIntro')}</p>
          {integrations === null ? (
            <span className={classes.help}>{t('ui.loading')}</span>
          ) : integrations.length === 0 ? (
            <span className={classes.help}>{t('form.addIntegration.noneYet')}</span>
          ) : (
            <ul className={classes.list}>
              {integrations.map((i) => (
                <li key={i.id} className={classes.item}>
                  <span className={classes.itemText}>
                    <strong>{i.title}</strong>
                    <span className={classes.help}>{note(i)}</span>
                  </span>
                  {linked.has(i.id) ? (
                    <Tag variant="ok">{t('form.addIntegration.linkedTag')}</Tag>
                  ) : (
                    <Button
                      size="xs"
                      loading={busyId === i.id}
                      onClick={() => void link(i)}
                      aria-label={t('form.addIntegration.linkLabel', { title: i.title })}
                    >
                      {t('form.addIntegration.link')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className={classes.createBox}>
            <span className={classes.sub}>{t('form.addIntegration.createIntro')}</span>
            <Button variant="filled" size="xs" onClick={() => setStep({ name: 'pick' })}>
              {t('form.addIntegration.createNew')}
            </Button>
          </div>
          <span className={classes.help}>{t('form.addIntegration.cost')}</span>
        </div>
      )}

      {step.name === 'pick' && (
        <div className={classes.stack}>
          <p className={classes.sub}>{t('form.addIntegration.pickIntro')}</p>
          <div className={classes.grid}>
            {ADAPTER_ORDER.filter((k) => getAdapterMeta(k)).map((k) => (
              <button
                key={k}
                type="button"
                className={classes.adapter}
                onClick={() => setStep({ name: 'config', kind: k })}
              >
                <strong>{adapterName(k, t)}</strong>
                <span className={classes.help}>{adapterBlurb(k, t)}</span>
              </button>
            ))}
          </div>
          <div>
            <Button variant="subtle" size="xs" onClick={() => setStep({ name: 'list' })}>
              {t('form.addIntegration.backToList')}
            </Button>
          </div>
        </div>
      )}

      {step.name === 'config' && (
        <IntegrationEditor
          kind={step.kind}
          mode="create"
          createLabel={t('form.addIntegration.createAndLink')}
          onBack={() => setStep({ name: 'pick' })}
          onCreate={(body) => create(step.kind, body)}
        />
      )}

      {step.name === 'secret' && (
        <SigningSecret
          secret={step.secret}
          signatureHeader={capabilitiesFor(caps, step.kind).signature_header}
          onDone={() => setStep({ name: 'list' })}
        />
      )}
    </Drawer>
  );
}
